import * as focusTrap from 'focus-trap';

const KEY_CODE = {
  ESC: 27,
};

function getTrendChecker({
  initialValue = 0,
  threshold = 20, // threshold required to trend change
  // onChange = (() => {}),
} = {}) {
  let lastValue = initialValue;
  let trendChangedValue = initialValue;
  let lastTrend = 1;
  let trend = 1;

  /**
   * @param {Number} value checked value
   * @returns {Number} 1 if value increasing -1 if value decreasing
   */
  return function update(value) {
    if (value === lastValue) return trend;

    const tmpTrend = value > lastValue ? 1 : -1;
    if (tmpTrend !== lastTrend) {
      trendChangedValue = lastValue;
      lastTrend = tmpTrend;
    }
    lastValue = value;

    if (lastTrend === trend) return trend;

    if (Math.abs(value - trendChangedValue) >= threshold) {
      trend = lastTrend; // real trend change
      // onChange(trend);
    }

    return trend;
  };
}

class Header {
  constructor($el) {
    this.$el = $el;
    this.$navigation = $el.querySelector('.header__navigation');
    this.$bottom = $el.querySelector('.header__bottom');
    this.$buttonsItems = $el.querySelector('.header__buttons-items');
    this.$modalButtons = $el.querySelectorAll('.header__button');

    this.$utils = $el.querySelector('.header__utils');
    this.$utilsDesktop = $el.querySelector('.header__utils--desktop');
    this.$utilsMobile = $el.querySelector('.header__utils--mobile');

    this.navigationFocusTrap = focusTrap.createFocusTrap(this.$el);

    this.initMediaQuery({
      navigation: '(min-width: 990px)',
    });

    this.classes = {
      menuItemExpanded: 'header__menu-item--expanded',
      utilItemExpanded: 'header__utils-lang-item--expanded',
      notTop: 'header--not-top',
      scrollingDown: 'header--scrolling-down',
      headerOverlay: 'header--overlay',
      hasOverlay: 'has-overlay',
      navigationOpened: 'header__navigation--opened',
      modalOpened: 'header__modal--opened',
      buttonActive: 'header__button--active',
      anyButtonActive: 'header__buttons-items--active',
    };

    this.expandedIndex = null;
    this.openedModalIndex = null;
    this.isCompact = false;
    this.scrollTrend = getTrendChecker();

    this.initMenuItems();
    this.initUtilItems();
    this.initModals();
    this.initEvents();
  }

  initMediaQuery(constraints) {
    this.mediaQuery = {};

    Object.keys(constraints).forEach((key) => {
      this.mediaQuery[key] = window.matchMedia(constraints[key]);
    });
  }

  initMenuItems() {
    this.bindMenuItemsEvents();

    const $menuItems = this.$navigation.querySelectorAll('.header__menu-item');

    this.menuItemObjs = [...$menuItems].map(($menuItem, index) => {
      const $menuLink = $menuItem.querySelector('.header__menu-link--has-children');
      if ($menuLink === null) return null;

      const $menuSub = $menuItem.querySelector('.header__menu-sub');
      const $menuClose = $menuSub.querySelector('.header__menu-sub-close');

      const menuItemObj = {
        index,
        $menuItem,
        $menuLink,
        $menuSub,
        $menuClose,
      };

      Header.setMenuItemAttrs(menuItemObj);
      this.setMenuItemEvents(menuItemObj);

      return menuItemObj;
    });
  }

  static setMenuItemAttrs({
    index,
    $menuItem,
    $menuLink,
    $menuSub,
    $menuClose,
  }) {
    $menuItem.setAttribute('data-menu-item-index', index);
    $menuLink.setAttribute('data-menu-item-index', index);
    $menuSub.setAttribute('data-menu-item-index', index);
    $menuClose.setAttribute('data-menu-item-index', index);

    $menuLink.setAttribute('aria-expanded', 'false');
    $menuLink.setAttribute('aria-controls', `menu-item-${index}`);
    $menuLink.setAttribute('id', `menu-link-${index}`);

    $menuSub.setAttribute('aria-labelledby', `menu-link-${index}`);
    $menuSub.setAttribute('id', `menu-item-${index}`);

    $menuClose.setAttribute('aria-controls', `menu-item-${index}`);
    $menuClose.setAttribute('id', `menu-close-${index}`);
  }

  bindMenuItemsEvents() {
    this.onMenuLinkClickEvent = this.onMenuLinkClick.bind(this);
    this.onMenuCloseClickEvent = this.onMenuCloseClick.bind(this);
  }

  setMenuItemEvents({ $menuLink, $menuClose }) {
    $menuLink.addEventListener('click', this.onMenuLinkClickEvent);
    $menuClose.addEventListener('click', this.onMenuCloseClickEvent);
  }

  onMenuLinkClick(e) {
    const menuItemObj = this.getMenuItemObj(e.currentTarget);
    if (menuItemObj === null) return;

    e.preventDefault();

    this.toggleMenuItem(menuItemObj);
  }

  onMenuCloseClick(e) {
    const menuItemObj = this.getMenuItemObj(e.currentTarget);
    if (menuItemObj === null) return;

    e.preventDefault();

    this.collapseMenuItem(menuItemObj);
  }

  /**
   * returns menuItemObj related with passed element
   * @param {Element} $el any element with data-menu-item-index attribute
   * @returns {Object|Null} null if not found
   */
  getMenuItemObj($el) {
    const index = $el.getAttribute('data-menu-item-index');
    if (index === null) return null;

    return this.getMenuItemObjByIndex(index);
  }

  /**
   * returns menuItemObj by index
   * @param {String|Number} $index index of menu item
   * @returns {Object|Null} null if not found
   */
  getMenuItemObjByIndex(index) {
    return this.menuItemObjs[parseInt(index, 10)] || null;
  }

  toggleMenuItem(menuItemObj) {
    if (menuItemObj.index === this.expandedIndex) {
      this.collapseMenuItem(menuItemObj);
    } else {
      this.expandMenuItem(menuItemObj);
    }
  }

  expandMenuItem({ index, $menuLink, $menuItem }) {
    if (this.expandedIndex === index) return;

    this.collapseExpandedItem();

    $menuItem.classList.add(this.classes.menuItemExpanded);
    $menuLink.setAttribute('aria-expanded', 'true');

    this.expandedIndex = index;
    this.setOverlay(true);
    this.navigationFocusTrap.activate();
    this.$navigation.addEventListener('keydown', this.onKeydownEvent);
    document.addEventListener('click', this.onClickOutsideNavEvent);
  }

  collapseMenuItem({ $menuLink, $menuItem }) {
    if (this.expandedIndex === null) return;

    $menuItem.classList.remove(this.classes.menuItemExpanded);
    $menuLink.setAttribute('aria-expanded', 'false');

    this.expandedIndex = null;
    this.setOverlay(false);
    this.navigationFocusTrap.deactivate();
    this.$navigation.removeEventListener('keydown', this.onKeydownEvent);
    document.removeEventListener('click', this.onClickOutsideNavEvent);
  }

  collapseExpandedItem() {
    if (this.expandedIndex === null) return;

    this.collapseMenuItem(this.getMenuItemObjByIndex(this.expandedIndex));
  }

  initModals() {
    this.bindModalEvents();

    this.modalObjs = [...this.$modalButtons].map(($modalButton, index) => {
      const modalId = $modalButton.getAttribute('data-modal');
      if (modalId === null) return null;

      const $modal = this.$el.querySelector(`[data-modal-id='${modalId}']`);
      if ($modal === null) return null;

      const $modalClose = $modal.querySelector('[data-modal-close]');

      const isMenu = (modalId === 'menu' || modalId === 'products');
      const openedClassName = (modalId === 'menu') ? this.classes.navigationOpened : this.classes.modalOpened;

      const modalObj = {
        index,
        $modal,
        $modalButton,
        $modalClose,
        isMenu,
        openedClass: openedClassName,
      };

      Header.setModalAttrs(modalObj);
      this.setModalEvents(modalObj);

      return modalObj;
    });
  }

  static setModalAttrs({
    index,
    $modal,
    $modalButton,
    $modalClose,
  }) {
    $modal.setAttribute('data-modal-index', index);
    $modalButton.setAttribute('data-modal-index', index);
    if ($modalClose !== null) $modalClose.setAttribute('data-modal-index', index);

    $modalButton.setAttribute('aria-expanded', 'false');
    $modalButton.setAttribute('aria-controls', `header-modal-${index}`);
    $modalButton.setAttribute('id', `header-modal-button-${index}`);

    $modal.setAttribute('aria-labelledby', `header-modal-button-${index}`);
    $modal.setAttribute('id', `header-modal-${index}`);
  }

  bindModalEvents() {
    this.onModalButtonClickEvent = this.onModalButtonClick.bind(this);
    this.onModalCloseClickEvent = this.onModalCloseClick.bind(this);
  }

  setModalEvents({ $modalButton, $modalClose }) {
    $modalButton.addEventListener('click', this.onModalButtonClickEvent);
    if ($modalClose !== null) $modalClose.addEventListener('click', this.onModalCloseClickEvent);
  }

  onModalButtonClick(e) {
    const modalObj = this.getModalObj(e.currentTarget);
    if (modalObj === null) return;

    e.preventDefault();

    this.toggleModal(modalObj);
  }

  onModalCloseClick(e) {
    const modalObj = this.getModalObj(e.currentTarget);
    if (modalObj === null) return;

    e.preventDefault();

    this.closeModal(modalObj);
  }

  /**
   * returns modalObj related with passed element
   * @param {Element} $el any element with data-modal-index attribute
   * @returns {Object|Null} null if not found
   */
  getModalObj($el) {
    const index = $el.getAttribute('data-modal-index');
    if (index === null) return null;

    return this.getModalObjByIndex(index);
  }

  /**
   * returns modalObj by index
   * @param {String|Number} $index index of menu item
   * @returns {Object|Null} null if not found
   */
  getModalObjByIndex(index) {
    return this.modalObjs[parseInt(index, 10)] || null;
  }

  toggleModal(modalObj) {
    if (modalObj.index === this.openedModalIndex) {
      this.closeModal(modalObj);
    } else {
      this.openModal(modalObj);
    }
  }

  openModal({
    index,
    $modalButton,
    $modal,
    isMenu,
    openedClass,
  }) {
    if (this.openedModalIndex === index) return;

    this.closeOpenedModal();

    if (isMenu && !this.mediaQuery.navigation.matches) {
      document.documentElement.classList.add(this.classes.hasOverlay);
      Header.moveChildNodes(this.$utilsDesktop, this.$utilsMobile);
    }

    $modal.classList.add(openedClass);
    $modalButton.classList.add(this.classes.buttonActive);
    $modalButton.setAttribute('aria-expanded', 'true');
    this.$buttonsItems.classList.add(this.classes.anyButtonActive);

    this.openedModalIndex = index;
    this.setOverlay(true);
    if (isMenu) {
      this.navigationFocusTrap.activate();
    }
    this.$bottom.addEventListener('keyup', this.onKeydownEvent);
    document.addEventListener('click', this.onClickOutsideModalsEvent);
  }

  closeModal({
    $modalButton,
    $modal,
    isMenu,
    openedClass,
  }) {
    if (this.openedModalIndex === null) return;

    document.documentElement.classList.remove(this.classes.hasOverlay);

    $modal.classList.remove(openedClass);
    $modalButton.classList.remove(this.classes.buttonActive);
    $modalButton.setAttribute('aria-expanded', 'false');
    this.$buttonsItems.classList.remove(this.classes.anyButtonActive);

    if (isMenu) {
      Header.moveChildNodes(this.$utilsMobile, this.$utilsDesktop);
    }

    this.openedModalIndex = null;
    this.setOverlay(false);
    if (isMenu) {
      this.navigationFocusTrap.deactivate();
    }
    this.$bottom.removeEventListener('keyup', this.onKeydownEvent);
    document.removeEventListener('click', this.onClickOutsideModalsEvent);
  }

  closeOpenedModal() {
    if (this.openedModalIndex === null) return;

    this.closeModal(this.getModalObjByIndex(this.openedModalIndex));
  }

  initEvents() {
    this.onClickOutsideNavEvent = this.onClickOutsideNav.bind(this);
    this.onClickOutsideModalsEvent = this.onClickOutsideModals.bind(this);
    this.onKeydownEvent = this.onKeydown.bind(this);

    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
  }

  onScroll() {
    const scrollTop = window.scrollY
      || window.pageYOffset
      || document.body.scrollTop
      || document.documentElement.scrollTop
      || 0;

    const isNotTop = scrollTop > this.$el.offsetHeight;
    const isScrollingDown = this.scrollTrend(scrollTop) === 1;

    Header.conditionalClassName(this.$el, this.classes.notTop, isNotTop);
    Header.conditionalClassName(this.$el, this.classes.scrollingDown, isScrollingDown);

    this.isCompact = isNotTop && isScrollingDown;
  }

  /**
   * conditional setting css class on element
   * @param {Element} $el target element
   * @param {String} className css class to add or remove
   * @param {Boolean} condition if true add else remove
   */
  static conditionalClassName($el, className, condition) {
    if (condition) {
      $el.classList.add(className);
    } else {
      $el.classList.remove(className);
    }
  }

  onClickOutsideNav(e) {
    if (this.expandedIndex === null) return;
    if (e.target.closest('.header__navigation') === this.$navigation) return;

    this.collapseExpandedItem();
  }

  onClickOutsideModals(e) {
    if (this.openedModalIndex === null) return;
    // if (e.target.closest('.header__bottom') === this.$bottom) return;

    if (!(
      this.mediaQuery.navigation.matches && !this.isCompact
      && e.target.closest('.header__navigation') === this.$navigation
    )) {
      if (e.target.closest('.header__bottom') === this.$bottom) return;
    }

    this.closeOpenedModal();
  }

  onKeydown(e) {
    if (e.keyCode !== KEY_CODE.ESC) return;

    if (this.expandedIndex !== null && e.currentTarget === this.$navigation) {
      this.collapseExpandedItem();
    } else if (this.openedModalIndex !== null && e.currentTarget === this.$bottom) {
      this.closeOpenedModal();
    }
  }

  static moveChildNodes($containerFrom, $containerTo) {
    while ($containerFrom.childNodes.length) {
      $containerTo.appendChild($containerFrom.firstChild);
    }
  }

  setOverlay(show) {
    if (show) {
      this.$el.classList.add(this.classes.headerOverlay);
    } else {
      if (this.openedModalIndex !== null || this.expandedIndex !== null) return;
      this.$el.classList.remove(this.classes.headerOverlay);
    }
  }

  initUtilItems() {
    const $utilLangItemLink = this.$utils.querySelector('.header__utils-lang-link');
    this.setUtilItemEvents($utilLangItemLink);
  }

  setUtilItemEvents($utilLink) {
    $utilLink.addEventListener('click', this.onUtilLinkClick.bind(this));
  }

  onUtilLinkClick(e) {
    const utilItem = e.currentTarget;
    if (utilItem === null) return;

    e.preventDefault();

    if (utilItem.getAttribute('aria-expanded') !== 'true') {
      utilItem.parentElement.classList.add(this.classes.utilItemExpanded);
      utilItem.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onUtilCloseClick.bind(this));
    } else {
      utilItem.parentElement.classList.remove(this.classes.utilItemExpanded);
      utilItem.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onUtilCloseClick.bind(this));
    }
  }

  onUtilCloseClick(e) {
    if (e.target.closest('.header__utils') === this.$utils) return;
    const utilItem = this.$utils.querySelector('.header__utils-lang-link');
    if (utilItem === null) return;

    e.preventDefault();

    utilItem.parentElement.classList.remove(this.classes.utilItemExpanded);
    utilItem.setAttribute('aria-expanded', 'false');

    document.removeEventListener('click', this.onUtilCloseClick.bind(this));
  }
}

document.querySelectorAll('.js-header').forEach(
  $el => new Header($el),
);
